import { Card } from "@material-ui/core";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { useEffect, useState } from "react";
import ApiCalls from "services/flick_erp_api_call";
import { Tooltip, IconButton, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Autocomplete, Checkbox } from "@mui/material";
import Info from "@mui/icons-material/Info";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { Add, Delete, Edit, RemoveRedEye, Save } from "@mui/icons-material";
import TextField from '@mui/material/TextField';
import { useLocation, useNavigate } from "react-router-dom";
import { store } from "../../../store";
import { setToastOpen } from "components/Toast/toastAlertSlice";

const SUMMARY_HEAD_GROUPS = {
  pf: ["epf", "epfe", "epfc"],
  tds: ["tds"],
  esic: ["esic", "esice", "esicc"],
  ptax: ["ptax"],
  gratuity: ["gratuity"],
};

const parseAmount = (value) => Number.parseFloat(value ?? 0) || 0;

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);

const normalizeText = (value = "") => String(value).trim().toLowerCase();

const getSummaryHeadKey = (line, headMap) => {
  const head = headMap.get(line.salary_head);
  const headValue = normalizeText(head?.head_value);
  const description = normalizeText(line.description);

  if (SUMMARY_HEAD_GROUPS.pf.includes(headValue) || description.includes("pf")) return "pf";
  if (SUMMARY_HEAD_GROUPS.tds.includes(headValue) || description.includes("tds")) return "tds";
  if (SUMMARY_HEAD_GROUPS.esic.includes(headValue) || description.includes("esic")) return "esic";
  if (SUMMARY_HEAD_GROUPS.ptax.includes(headValue) || description.includes("professional tax") || description.includes("ptax")) return "ptax";
  if (SUMMARY_HEAD_GROUPS.gratuity.includes(headValue) || description.includes("gratuity")) return "gratuity";
  return null;
};

function SalaryBatches() {

  const location = useLocation();
  const navigate = useNavigate();
  const batchData = location.state?.batchData;
  const queryParams = new URLSearchParams(location.search);
  const batchId = batchData?.id || queryParams.get("batchId");
  const permissions = location.state?.permissions;
  const modulePermissions = location?.state?.modulePermissions;

  const [batchRecords, setBatchRecords] = useState([]);
  const [status, setStatus] = useState({ val: "active", name: "Active" });
  const [openDialog, setOpenDialog] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [salaryHeads, setSalaryHeads] = useState([])
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeData, setEmployeeData] = useState(null)
  const [selectedEmployeeId, setSelectedEmployeeID] = useState(null);
  const [selectedSalaryLine, setSelectedSalaryLine] = useState(null);
  const [salaryLines, setSalaryLines] = useState(selectedEmployee?.salary_lines || []);
  const isBatchPublished = Boolean(batchData?.is_published || employeeData?.batch?.is_published);

  const showLockedMessage = (message = "This payroll batch is published and cannot be modified.") => {
    store.dispatch(
      setToastOpen({
        isOpen: true,
        message,
      })
    );
  };


  const [batch, setBatch] = useState({
    columns: [
      {
        Header: "Emp Id",
        accessor: "id",
      },
      {
        Header: "Employee Name",
        accessor: "firstname",
        Cell: ({ row }) => (
          <div
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={(e) => {
              // navigate(`/Payrole/payroleBatches?batch_details`);
              setSelectedEmployeeID(row?.original?.batchId)
              handleEmployeeBatchDetail(e, row)
            }}
          >
            <span style={{ marginLeft: "0.5rem" }}>
              {row.original.firstname}
            </span>
            <span style={{ marginLeft: "0.2rem" }}>
              {row.original.lastname}
            </span>
          </div>
        ),
      },
      { Header: "Total Days", accessor: "total_days" },
      { Header: "Working Days", accessor: "working_days" },
      { Header: "Net Pay", accessor: "net_pay" },
    ],
    rows: batchRecords,
  });
  const [updatedSalaryHead, setUpdatedSalaryHead] = useState(null);
  const [updateEmployeeData, setUpdateEmployeeData] = useState(selectedEmployee);
  const [newSalaryLines, setNewSalaryLines] = useState([{ name: '', description: '', amount: '' }]);

  useEffect(() => {
    getSalarySlips();
    getSalaryHeads();
  }, []);

  const salaryHeadMap = new Map(salaryHeads.map((head) => [head.id, head]));
  const payrollSummary = batchRecords.reduce(
    (summary, record) => {
      summary.netSalary += parseAmount(record.net_pay);
      summary.grossSalary += parseAmount(record.gross_pay);

      (record.salary_lines || []).forEach((line) => {
        const amount = parseAmount(line.amount);
        const summaryKey = getSummaryHeadKey(line, salaryHeadMap);

        if (summaryKey) {
          summary[summaryKey] += amount;
          return;
        }

        const head = salaryHeadMap.get(line.salary_head);
        if (normalizeText(head?.head_type) === "deduction") {
          summary.otherDeductions += amount;
        }
      });

      return summary;
    },
    {
      netSalary: 0,
      grossSalary: 0,
      pf: 0,
      tds: 0,
      esic: 0,
      ptax: 0,
      gratuity: 0,
      otherDeductions: 0,
    }
  );

  const summaryCards = [
    { label: "Net Salary", value: payrollSummary.netSalary, helper: "Final payout for this batch", color: "#1b5e20", bg: "#e8f5e9" },
    { label: "Gross Salary", value: payrollSummary.grossSalary, helper: "Before deductions", color: "#0d47a1", bg: "#e3f2fd" },
    { label: "PF", value: payrollSummary.pf, helper: "Employee and employer PF lines", color: "#6a1b9a", bg: "#f3e5f5" },
    { label: "TDS", value: payrollSummary.tds, helper: "Income tax deducted", color: "#bf360c", bg: "#fbe9e7" },
    { label: "ESIC", value: payrollSummary.esic, helper: "ESIC contributions", color: "#004d40", bg: "#e0f2f1" },
    { label: "PTAX", value: payrollSummary.ptax, helper: "Professional tax", color: "#4e342e", bg: "#efebe9" },
    { label: "Gratuity", value: payrollSummary.gratuity, helper: "Gratuity allocation", color: "#827717", bg: "#f9fbe7" },
    { label: "Other Deductions", value: payrollSummary.otherDeductions, helper: "LOP, leave and other deductions", color: "#b71c1c", bg: "#ffebee" },
  ];

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.get("popup")) {
      setOpenDialog(true);
    }
  
    const handleBack = () => {
      if (searchParams.get("popup") === "true") {
        setOpenDialog(false);

        // Remove the query param and update history
        searchParams.delete("popup");
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
      }
    };
  
    window.addEventListener("popstate", handleBack);
  
    return () => {
      window.removeEventListener("popstate", handleBack);
    };
  }, [location.search, navigate]);

  async function getSalarySlips() {
    const res = await ApiCalls.getSalary(`?batch=${batchId}`);
    if (res.success && res.code === 200) {
      setBatchRecords(res.data);
    }
  }

  async function getEmployeeData(row) {
   
    const res = await ApiCalls.getSalary(`?employee=${row?.original?.emp_id?.id}`);
  
    if (res.success && res.code === 200) {
      let data = res.data;
      
      // Ensure batchId is a string before comparison
      let selectedEmployeeData = data.find((item) => {
        return String(item?.batch?.id) === String(batchId);
      });
      setEmployeeData(selectedEmployeeData)
      if(selectedEmployee){
        selectedEmployee.salary_lines = selectedEmployeeData?.salary_lines;
        setSelectedEmployee(selectedEmployee)
      }
      
     
    }
  }

  async function getSalaryHeads() {
    var res = await ApiCalls.getSalaryHead();
    if (res.success && res.code == 200) {
      setSalaryHeads(res.data);
    }
  }
  

  const handleEmployeeBatchDetail = (e, row) => {
    setSelectedEmployee(row.original);
    setUpdatedSalaryHead(row?.original?.salary_lines)
    setSalaryLines(row?.original?.salary_lines)
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("popup", "true");
    navigate(`${location.pathname}?${searchParams.toString()}`);
    getEmployeeData(row)
    setOpenDialog(true);
  };

  const handleActionClick = (e, head, action) => {
    const matchedHead = salaryHeads.find((item) => item.description === head.description);
    if (matchedHead) {
      setSelectedSalaryLine(matchedHead);
    } else {
      setSelectedSalaryLine(head);
    }

    setCurrentAction(action);
    setOpenActionDialog(true);
  };


  const handleCloseActionDialog = () => {
    setOpenActionDialog(false);
    setCurrentAction(null);
    setSelectedSalaryLine(null);
  }

  const handleCloseDialog = () => {
    setSelectedEmployee(null);
    setUpdateEmployeeData(null);
    setUpdatedSalaryHead(null);
    setOpenDialog(false);

    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("popup");
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  const handleDeleteSalaryLine = async (head) => {
    if (isBatchPublished) {
      showLockedMessage();
      return;
    }
    try {
      const response = await ApiCalls.deleteSalaryLine(head.id);
      if (response?.success) {
        const updatedSalaryLines = salaryLines.filter((line) => line.id !== head.id);
        setSalaryLines(updatedSalaryLines);
      }
    } catch (error) {
      console.error("Error updating salary line:", error);
    }
  };


  useEffect(() => {
    setBatch((prevBatch) => ({
      ...prevBatch,
      rows: batchRecords
        .filter((record) => record.contract.is_active === true) // Filter based on selected status
        .map((record, index) => (
          {
            batchId: record.id,
            id: record.contract?.employee?.user?.id,
            firstname: record.contract?.employee.user.firstname,
            lastname: record.contract?.employee.user.lastname,
            total_days: record.total_days,
            working_days: record.working_days,
            net_pay: record.net_pay,
            gross_pay: record.gross_pay,
            paid_leave: record.paid_leave,
            unpaid_leave: record.unpaid_leave,
            // profile_photo: record.employee?.img,
            absent_days: record.absent_days,
            total_deductions: record.total_deductions,
            salary_lines: record.salary_lines,
            emp_id: record.employee,
            is_published: record.batch?.is_published,
          })),
    }));
  }, [batchRecords, status]);

  const handleInputChange = (field, value) => {
    setSelectedEmployee((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  async function handleSaveClick() {
    if (isBatchPublished) {
      showLockedMessage();
      return;
    }
    
    let payload = {
          total_days: selectedEmployee?.total_days,
          working_days: selectedEmployee?.working_days,
          paid_leave: selectedEmployee.paid_leave,
          unpaid_leave: selectedEmployee?.unpaid_leave,
          absent_days: selectedEmployee?.absent_days,
          gross_pay: selectedEmployee?.gross_pay,
          total_deductions: selectedEmployee?.total_days,
          net_pay: selectedEmployee?.net_pay,
          is_finalized: true,
          org: employeeData?.org,
          batch: employeeData?.batch?.id,
          employee: employeeData?.employee,
          contract: employeeData?.contract?.id
        }
        const res = await ApiCalls.editSalary(
          employeeData?.id,
          payload
        );
        if (res.success && res.code == 200) {
          getSalarySlips();
          handleCloseDialog();
        } else {
        }
  }

  const handleAddNewSalaryLine = () => {
    if (isBatchPublished) {
      showLockedMessage();
      return;
    }
    setNewSalaryLines([...newSalaryLines, { name: "", description: "", amount: "" }]);
  };

  const handleSaveNewSalaryLine = async (index) => {
    if (isBatchPublished) {
      showLockedMessage();
      return;
    }
    const newLine = newSalaryLines[index];
    const salaryHead = salaryHeads.find(
      (head) => head.name === newLine?.name
    );
    if (!salaryHead) {
      console.error("Error: Salary head not found for", newLine?.salaryName);
      return;
    }

    let payload = {
      description: newLine?.description,
      amount: newLine?.amount,
      employee_salary: selectedEmployee?.batchId,
      salary_head: salaryHead.id,
    }

    try {
      const response = await ApiCalls.createSalaryLine(payload, false); // Replace with your actual API call
      if (response.success) {
        setSalaryLines([...salaryLines, response.data]);
        setNewSalaryLines(newSalaryLines.filter((_, i) => i !== index));
      }
    } catch (error) {
      console.error("Error saving salary line:", error);
    }
  };

  const handleNewSalaryLineChange = (index, field, value) => {
    const updatedNewLines = [...newSalaryLines];
    updatedNewLines[index][field] = value;
    setNewSalaryLines(updatedNewLines);
  };

  const handleUpdateSalaryLine = async (head) => {
    if (isBatchPublished) {
      showLockedMessage();
      return;
    }
    try {
      if (!head.id) {
        console.error("Salary line is missing an ID.");
        return;
      }

      const salaryHead = salaryHeads.find(
        (head) => head.name === head?.name
      );

      const payload = {
        salary_head: salaryHead.id,
        description: head.description,
        amount: head.amount,
        employee_salary: selectedEmployee?.batchId
      };


      const response = await ApiCalls.patchSalaryLine(head.id, payload, false);
      if (response?.success) {
        setSalaryLines([...salaryLines]);
      }
    } catch (error) {
      console.error("Error updating salary line:", error);
    }
  };


  const renderEmployeeBatchDetails = () => {
    return (
      <Dialog
        fullWidth
        maxWidth="lg"
        open={openDialog}
        onClose={handleCloseDialog}
      >
        <DialogTitle>Employee Batch Details</DialogTitle>
        <DialogContent>
          {selectedEmployee && (
            <MDBox sx={{ mt: 2 }}>
              {isBatchPublished && (
                <MDBox mb={2} px={2} py={1.25} borderRadius="lg" sx={{ backgroundColor: "#e8f5e9" }}>
                  <MDTypography variant="button" fontWeight="medium" color="success">
                    Published payroll is locked. Salary values and salary lines cannot be edited.
                  </MDTypography>
                </MDBox>
              )}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <MDTypography variant='h6'>First Name</MDTypography>
                  <MDInput
                    fullWidth
                    value={`${selectedEmployee.firstname} `}
                    disabled={isBatchPublished}
                    onChange={(e) => handleInputChange("firstname", e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant='h6'>Last Name</MDTypography>
                  <MDInput
                    fullWidth
                    value={`${selectedEmployee.lastname}`}
                    disabled={isBatchPublished}
                    onChange={(e) => handleInputChange("lastname", e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant='h6'>Total Days</MDTypography>
                  <MDInput disabled={isBatchPublished} fullWidth value={updateEmployeeData?.total_days || selectedEmployee.total_days} onChange={(e) => handleInputChange("total_days", e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant='h6'>Working Days</MDTypography>
                  <MDInput disabled={isBatchPublished} fullWidth value={updateEmployeeData?.working_days || selectedEmployee.working_days} onChange={(e) => handleInputChange("working_days", e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant='h6'>Paid Leave</MDTypography>
                  <MDInput disabled={isBatchPublished} fullWidth value={updateEmployeeData?.paid_leave || selectedEmployee.paid_leave} onChange={(e) => handleInputChange("paid_leave", e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant='h6'>Unpaid Leave</MDTypography>
                  <MDInput disabled={isBatchPublished} fullWidth value={updateEmployeeData?.unpaid_leave || selectedEmployee.unpaid_leave} onChange={(e) => handleInputChange("unpaid_leave", e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant='h6'>Absent Days</MDTypography>
                  <MDInput disabled={isBatchPublished} fullWidth value={updateEmployeeData?.absent_days || selectedEmployee.absent_days} onChange={(e) => handleInputChange("absent_days", e.target.value)} />
                </Grid>
              </Grid>

              <MDTypography variant="h5" mt={4} mb={2}>
                Payment Details
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <MDTypography variant='h6'>Gross Pay</MDTypography>
                  <MDInput disabled={isBatchPublished} fullWidth value={updateEmployeeData?.gross_pay || selectedEmployee.gross_pay} onChange={(e) => handleInputChange("gross_pay", e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant='h6'>Total Deductions</MDTypography>
                  <MDInput disabled={isBatchPublished} fullWidth value={updateEmployeeData?.total_deductions || selectedEmployee.total_deductions} onChange={(e) => handleInputChange("total_deductions", e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant='h6'>Net Pay</MDTypography>
                  <MDInput disabled={isBatchPublished} fullWidth value={updateEmployeeData?.net_pay || selectedEmployee.net_pay} onChange={(e) => handleInputChange("net_pay", e.target.value)} />
                </Grid>
              </Grid>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h5" mt={4} mb={2}>
                  Salary Heads
                </MDTypography>
                {/* <MDButton variant="gradient" color="dark" onClick={handleAddSalaryLine} size='small'>
                  Add
                </MDButton> */}
                <MDButton variant="gradient" color="dark" onClick={handleAddNewSalaryLine} size="small" disabled={isBatchPublished}>
                  Add
                </MDButton>
              </MDBox>
              <Grid container spacing={1} sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold", p: 1 }}>
                <Grid item xs={3}>
                  <MDTypography variant="h6">Salary Head</MDTypography>
                </Grid>
                <Grid item xs={3}>
                  <MDTypography variant="h6">Description</MDTypography>
                </Grid>
                <Grid item xs={3}>
                  <MDTypography variant="h6">Amount</MDTypography>
                </Grid>
                <Grid item xs={3}>
                  <MDTypography variant="h6">Action</MDTypography>
                </Grid>
              </Grid>
              {salaryLines?.map((head, index) => (
                // {salaryLines?.map((head, index) => (
                <Grid
                  container
                  key={index}
                  spacing={1}
                  sx={{ borderBottom: "1px solid #ddd", p: 1, alignItems: "center" }}
                >
                  <Grid item xs={3}>
                    <Autocomplete
                      disabled={isBatchPublished}
                      options={salaryHeads.map((item) => item.name)}
                      getOptionLabel={(option) => option || ""}
                      renderInput={(params) => (
                        <TextField {...params} label="Select Head" variant="outlined" />
                      )}
                      value={salaryHeads.find((item) => item.id === head.salary_head)?.name || ""}
                      onChange={(event, newValue) => {
                        const updatedSalaryLines = [...salaryLines];
                        updatedSalaryLines[index].description = newValue;
                        updatedSalaryLines[index].name = newValue;
                        setSalaryLines(updatedSalaryLines);
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <MDInput
                      disabled={isBatchPublished}
                      placeholder='Description'
                      fullWidth
                      value={head.description}
                      onChange={(e) => {
                        const updatedSalaryLines = [...salaryLines];
                        updatedSalaryLines[index].description = e.target.value;
                        setSalaryLines(updatedSalaryLines);
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <MDInput
                      disabled={isBatchPublished}
                      fullWidth
                      placeholder='Add amount'
                      value={head.amount}
                      onChange={(e) => {
                        const updatedSalaryLines = [...salaryLines];
                        updatedSalaryLines[index].amount = e.target.value;
                        setSalaryLines(updatedSalaryLines);
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <MDBox sx={{ display: "flex" }}>
                      <IconButton onClick={() => handleDeleteSalaryLine(head)} disabled={isBatchPublished}>
                        <Delete />
                      </IconButton>
                      <IconButton onClick={() => handleUpdateSalaryLine(head, index)} disabled={isBatchPublished}>
                        <Save />
                      </IconButton>
                    </MDBox>

                    {/* <MDBox sx={{ display: "flex", alignItems: "center" }}>
                      <Checkbox
                        checked={markedForDeletion.has(head.id)}
                        onChange={(e) => {
                          const updatedDeletionSet = new Set(markedForDeletion);
                          if (e.target.checked) {
                            updatedDeletionSet.add(head.id);
                          } else {
                            updatedDeletionSet.delete(head.id);
                          }
                          setMarkedForDeletion(updatedDeletionSet);
                        }}
                      />
                    </MDBox> */}
                  </Grid>
                </Grid>
              ))}
              {newSalaryLines?.map((line, index) => (
                <Grid container key={`new-${index}`} spacing={1} sx={{ borderBottom: "1px solid #ddd", p: 1, alignItems: "center" }}>
                  <Grid item xs={3}>
                    <Autocomplete
                      disabled={isBatchPublished}
                      options={salaryHeads.map((item) => item.name)}
                      renderInput={(params) => <TextField {...params} label="Select Head" variant="outlined" />}
                      value={line.name}
                      onChange={(_, newValue) => handleNewSalaryLineChange(index, "name", newValue || "")}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <MDInput
                      disabled={isBatchPublished}
                      placeholder="Description"
                      fullWidth
                      value={line.description}
                      onChange={(e) => handleNewSalaryLineChange(index, "description", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <MDInput
                      disabled={isBatchPublished}
                      fullWidth
                      placeholder="Add amount"
                      value={line.amount}
                      onChange={(e) => handleNewSalaryLineChange(index, "amount", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <IconButton onClick={() => handleSaveNewSalaryLine(index)} disabled={isBatchPublished}>
                      <Add />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}


            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSaveClick} color="primary" disabled={isBatchPublished}>
            Save
          </Button>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <MDBox mb={3}>
          <Card>
            <MDBox
              p={3}
              lineHeight={1}
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <MDTypography variant="h5" fontWeight="medium">
                Salary Batches for {batchData?.batch}
              </MDTypography>
              <MDBox
                px={1.5}
                py={0.5}
                borderRadius="xl"
                bgColor={batchData?.is_published ? "success" : "warning"}
                display="inline-block"
              >
                <MDTypography variant="caption" color="white" fontWeight="bold">
                  {batchData?.is_published ? "Published" : "Draft"}
                </MDTypography>
              </MDBox>
            </MDBox>
            <MDBox px={3} pb={3}>
              <Grid container spacing={2}>
                {summaryCards.map((item) => (
                  <Grid item xs={12} sm={6} md={3} key={item.label}>
                    <Tooltip title={item.helper} arrow>
                      <MDBox
                        px={2}
                        py={1.75}
                        borderRadius="xl"
                        sx={{
                          backgroundColor: item.bg,
                          border: `1px solid ${item.color}22`,
                          minHeight: "100%",
                        }}
                      >
                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          sx={{ color: item.color, letterSpacing: "0.08em" }}
                        >
                          {item.label}
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="bold" mt={0.5}>
                          {formatCurrency(item.value)}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" opacity={0.8}>
                          {item.helper}
                        </MDTypography>
                      </MDBox>
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
            </MDBox>
            <MDBox>
              <DataTable
                table={batch}
                canSearch
                // permissions={permissions}
                sort={
                  // permissions.includes(modulePermissions.update_permission) &&(
                  <Autocomplete
                    value={status}
                    options={[{ val: "active", name: "Active" }, { val: "deactive", name: "Deactive" }, { val: "notish", name: "In Notice" }]}
                    getOptionLabel={(e) => e?.name}
                    onChange={(event, newValue) => {
                      setStatus(newValue ?? { val: "active", name: "Active" });
                    }}
                    sx={{ width: "240px", paddingLeft: "5px", paddingRight: "5px" }}
                    renderInput={(params) => (
                      <TextField {...params} variant="standard" InputProps={{ ...params.InputProps, disableUnderline: true }}
                      />
                    )}
                  />}
              ></DataTable>
            </MDBox>
          </Card>

        </MDBox>
      </MDBox>

      {/* Dialog Component */}
      {openDialog && renderEmployeeBatchDetails()}

      <Dialog
        fullWidth
        maxWidth="lg"
        open={openActionDialog}
        onClose={handleCloseActionDialog}
      >
        <DialogTitle>
          {currentAction === "edit"
            ? "Edit Salary Head"
            : currentAction === "add"
              ? "Add Salary Head"
              : "View Salary Head"}
        </DialogTitle>

        <DialogContent>
          <MDBox mt={2} >
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={3}>
                <MDTypography variant="h6">Salary Head Name</MDTypography>
              </Grid>
              <Grid item xs={9}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={selectedSalaryLine?.description || ""}
                  disabled={currentAction === "view"}
                  placeholder="Enter Salary Head Name"
                />
              </Grid>
            </Grid><br />
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={3}>
                <MDTypography variant="h6">Head Code</MDTypography>
              </Grid>
              <Grid item xs={9}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={selectedSalaryLine?.description || ""}
                  disabled={currentAction === "view"}
                  placeholder="Enter Salary Head Name"
                />
              </Grid>
            </Grid><br />
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={3}>
                <MDTypography variant="h6">Organization</MDTypography>
              </Grid>
              <Grid item xs={9}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={selectedSalaryLine?.name || ""}
                  disabled={currentAction === "view"}
                  placeholder="Enter Salary Head Name"
                />
              </Grid>
            </Grid><br />
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={3}>
                <MDTypography variant="h6"> Head Type</MDTypography>
              </Grid>
              <Grid item xs={9}>
                <Autocomplete
                  options={["Deduction", "Allowance"]}
                  getOptionLabel={(option) => option || ""}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Salary Type" variant="outlined" />
                  )}
                  value={selectedSalaryLine?.description || ""}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelectedSalaryLine((prev) => ({
                        ...prev,
                        description: newValue,
                      }));
                    }
                  }}
                  disabled={currentAction === "view"}
                />
              </Grid>
            </Grid><br />
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={3}>
                <MDTypography variant="h6">Head Value</MDTypography>
              </Grid>
              <Grid item xs={9}>
                <Autocomplete
                  options={salaryHeads.map((item) => item.name)} // Assuming salaryHeads is a list of available salary head options
                  getOptionLabel={(option) => option || ""}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Salary Head" variant="outlined" />
                  )}
                  value={selectedSalaryLine?.description || ""}
                // onChange={(event, newValue) => {
                //     const updatedSalaryLines = [...selectedEmployee.salary_lines];
                //     updatedSalaryLines[index].name = newValue;
                //     setSelectedEmployee((prev) => ({
                //         ...prev,
                //         salary_lines: updatedSalaryLines,
                //     }));
                // }}
                />
              </Grid>
            </Grid><br />
          </MDBox>
        </DialogContent>


        <DialogActions>
          <Button onClick={handleCloseActionDialog} color="primary">
            Close
          </Button>

          {currentAction !== "view" && (
            <Button
              // onClick={handleSaveHead}
              color="primary"
              variant="contained"
            >
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>


    </DashboardLayout>
  );
}

export default SalaryBatches;
